import { DateTime } from 'luxon';

const optionValue = option => option?.value ?? option?.id ?? option;

const normalizeEnvironmentQuery = environmentDataQuery => {
  if (!environmentDataQuery) return null;
  if (typeof environmentDataQuery !== 'string') return environmentDataQuery;
  try {
    return JSON.parse(environmentDataQuery);
  } catch (error) {
    return null;
  }
};

const getDefaultDateRange = () => {
  const endDate = new Date();
  endDate.setSeconds(0, 0);
  return {
    startDate: new Date(endDate.getTime() - 72 * 60 * 60 * 1000),
    endDate,
  };
};

export const selectionToEventFilter = (selection, overrides = {}) => ({
  count: 0,
  skip: 0,
  eventSortType: 'ACTIVATED',
  cars: selection?.consist?.cars?.length
    ? [...selection.consist.cars]
    : (selection?.cars || []).map(optionValue),
  sortReverse: true,
  searchString: null,
  startDate: selection?.startDate ? DateTime.fromJSDate(selection.startDate).toUTC().toISO() : null,
  endDate: selection?.endDate ? DateTime.fromJSDate(selection.endDate).toUTC().toISO() : null,
  subsystemIds: (selection?.subsystems || []).map(optionValue),
  eventIds: (selection?.events || []).map(optionValue),
  consist: selection?.consist ? optionValue(selection.consist) : null,
  localTimezone: DateTime.local().zoneName,
  environmentDataQuery: overrides.environmentDataQuery ?? selection?.envFilters?.filterJson ?? null,
  ...overrides,
});

export const selectionFingerprint = selection => JSON.stringify({
  cars: (selection?.cars || []).map(optionValue),
  consist: selection?.consist ? optionValue(selection.consist) : null,
  subsystems: (selection?.subsystems || []).map(optionValue),
  events: (selection?.events || []).map(optionValue),
  dataWindowMode: selection?.dataWindowMode || null,
  dataWindowValue: selection?.dataWindowValue ?? null,
  startDate: selection?.startDate?.toISOString?.() || null,
  endDate: selection?.endDate?.toISOString?.() || null,
  environmentDataQuery: selection?.envFilters?.filterJson || null,
});

export const createSelection = ({ rule = {}, consistOptions = [], carOptions = [], subsystemOptions = [], eventOptions = [] } = {}) => {
  const defaultDateRange = getDefaultDateRange();
  const dataWindowSeconds = Number(rule.dataWindowSeconds);
  const hasSavedDataWindow = dataWindowSeconds > 0;

  return {
    startDate: rule.startDate ? new Date(rule.startDate) : defaultDateRange.startDate,
    endDate: rule.endDate ? new Date(rule.endDate) : defaultDateRange.endDate,
    cars: carOptions.filter(option => (rule.cars || []).map(String).includes(String(optionValue(option)))),
    subsystems: subsystemOptions.filter(option => (rule.subsystemIds || []).map(String).includes(String(optionValue(option)))),
    events: eventOptions.filter(option => (rule.eventNumbers || rule.eventIds || []).map(String).includes(String(optionValue(option)))),
    consist: consistOptions.find(option => String(optionValue(option)) === String(rule.consist || rule.consists?.[0])) || null,
    envFilters: {
      query: normalizeEnvironmentQuery(rule.environmentDataQuery) || { combinator: 'and', rules: [] },
      filterJson: normalizeEnvironmentQuery(rule.environmentDataQuery) || '',
    },
    dataWindowMode: hasSavedDataWindow ? getDataWindowUnit(dataWindowSeconds) : 'date',
    dataWindowValue: hasSavedDataWindow
      ? formatDataWindowValue(dataWindowSecondsToValue(dataWindowSeconds, getDataWindowUnit(dataWindowSeconds)))
      : null,
    dataWindowSeconds: hasSavedDataWindow ? dataWindowSeconds : null,
  };
};

export const selectionToDataWindowSeconds = (selection, fallbackHours = 72) => {
  const mode = selection?.dataWindowMode;
  const value = Number(selection?.dataWindowValue);
  const savedDataWindowSeconds = Number(selection?.dataWindowSeconds);
  if (savedDataWindowSeconds > 0) return Math.max(1, Math.round(savedDataWindowSeconds));

  if (mode !== 'date' && value > 0 && dataWindowUnitToSeconds[mode]) {
    return Math.max(1, Math.round(value * dataWindowUnitToSeconds[mode]));
  }

  if (selection?.startDate && selection?.endDate) {
    const rangeSeconds = (new Date(selection.endDate).getTime() - new Date(selection.startDate).getTime()) / 1000;
    if (rangeSeconds > 0) return Math.max(1, Math.round(rangeSeconds));
  }

  return Math.max(1, Math.round(Number(fallbackHours) * 60 * 60));
};

export const dataWindowUnitToSeconds = {
  seconds: 1,
  minutes: 60,
  hours: 60 * 60,
  day: 24 * 60 * 60,
  days: 24 * 60 * 60,
};

export const MAX_DATA_WINDOW_DAYS = 31;
export const MAX_DATA_WINDOW_SECONDS = MAX_DATA_WINDOW_DAYS * dataWindowUnitToSeconds.day;

// Backend 400s for alarm rules come back as plain text such as "dataWindowSeconds should be smaller than 31 days".
export const getAlarmRuleErrorMessage = (error, fallback) => {
  const data = error?.data;
  const rawMessage = [
    error?.errorMessage,
    data?.errorMessage,
    typeof data === 'string' ? data : data?.message,
  ].find(message => typeof message === 'string' && message.trim());
  if (!rawMessage) return fallback;
  const message = rawMessage.trim().replace(/\bdataWindowSeconds\b/g, 'The data window');
  return /[.!?]$/.test(message) ? message : `${message}.`;
};

export const dataWindowUnitLabels = {
  seconds: 'seconds',
  minutes: 'minutes',
  hours: 'hours',
  day: 'days',
  days: 'days',
};

export const getDataWindowUnit = dataWindowSeconds => {
  const seconds = Number(dataWindowSeconds);
  if (seconds >= 72 * dataWindowUnitToSeconds.hours) return 'day';
  if (seconds >= 360 * dataWindowUnitToSeconds.minutes) return 'hours';
  if (seconds >= 360 * dataWindowUnitToSeconds.seconds) return 'minutes';
  return 'seconds';
};

export const dataWindowSecondsToValue = (dataWindowSeconds, unit) => {
  const unitSeconds = dataWindowUnitToSeconds[unit];
  return unitSeconds ? Number(dataWindowSeconds) / unitSeconds : Number(dataWindowSeconds);
};

export const formatDataWindowValue = value => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 0;
  return Number(numericValue.toFixed(2));
};